import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Coins,
  ArrowRight,
  Sparkles,
  Gift,
  Users,
  ShieldCheck,
  Clock,
  BadgePercent,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TruCoinBadge } from "@/components/TruCoinBadge";
import { Reveal } from "@/components/Reveal";
import { ReviewDisclosure } from "@/components/reviews/ReviewDisclosure";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/rewards")({
  component: RewardsLanding,
  head: () => ({
    meta: [
      { title: "TruCoins Rewards. Get 5% Back on Every TruWe Order" },
      {
        name: "description",
        content:
          "Join TruCoins and earn 5% back on every prepaid TruWe order. 1 TruCoin = ₹0.50 off your next order.",
      },
      { property: "og:title", content: "TruCoins Rewards, 5% Back on Every TruWe Order" },
      {
        property: "og:description",
        content:
          "Earn 5% back as TruCoins on every prepaid order and redeem as instant discounts.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "TruWe" },
      { property: "og:locale", content: "en_IN" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@truwe" },
      { name: "twitter:title", content: "TruCoins Rewards, 5% Back on Every TruWe Order" },
      {
        name: "twitter:description",
        content:
          "Earn 5% back as TruCoins on every prepaid order and redeem as instant discounts at checkout.",
      },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/rewards" }],
  }),
});

function RewardsLanding() {
  return (
    <main className="min-h-screen bg-background">
      <Hero />
      <StatsStrip />
      <TierLadder />
      <ThreeStep />
      <section className="mx-auto max-w-3xl px-5 pb-4">
        <ReviewDisclosure />
      </section>
      <TrustGrid />

      <FinalCta />
    </main>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden pt-16 pb-16 md:pt-24 md:pb-24">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 620px at 78% 8%, color-mix(in oklab, var(--brand) 20%, transparent), transparent 62%), radial-gradient(620px 520px at 8% 92%, color-mix(in oklab, var(--brand) 12%, transparent), transparent 62%)",
        }}
      />
      <div className="container relative mx-auto grid gap-12 px-4 md:grid-cols-2 md:items-center md:px-8">
        <Reveal>
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-white/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" />
              Membership is free
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-[1.03] tracking-tight text-brand-deep md:text-6xl">
              Get <span className="text-brand">5% back</span> on every TruWe order
            </h1>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
              TruCoins are TruWe&rsquo;s rewards currency. Every prepaid order earns coins, and every
              coin is worth ₹0.50 off your next order. No points maths, no expiry surprises.
            </p>
            <ul className="mt-6 grid gap-2.5">
              {[
                "1 TruCoin = ₹0.50 off, always",
                "50 TruCoins for every review you write, any rating",
                "Redeem from just 20 coins (₹10 off)",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5 text-sm text-brand-deep">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                    <Check className="h-3 w-3" />
                  </span>
                  {line}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/shop">
                <Button size="lg" className="w-full rounded-full px-7 sm:w-auto">
                  Start earning
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/trucoins">
                <Button variant="outline" size="lg" className="w-full rounded-full px-7 sm:w-auto">
                  How it works
                </Button>
              </Link>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="relative mx-auto w-full max-w-sm">
            <div className="glass-panel rounded-[2rem] p-8 text-center shadow-luxury">
              <div className="flex justify-center">
                <TruCoinBadge size="lg" className="float-soft" />
              </div>
              <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                Example order
              </p>
              <p className="mt-1 font-display text-3xl font-bold text-brand-deep">₹1,999</p>
              <div className="mt-6 space-y-3 text-left">
                <ExampleRow label="You earn" value="99 TruCoins" />
                <ExampleRow label="Worth" value="₹49.50 off" highlight />
              </div>
              <p className="mt-6 text-xs text-muted-foreground">
                Illustrative. Coins are calculated on ex-GST merchandise value.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ExampleRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between rounded-xl border px-4 py-3 text-sm",
        highlight ? "border-brand/30 bg-brand-soft/50" : "border-border bg-white/70",
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-display font-semibold", highlight ? "text-brand" : "text-brand-deep")}>
        {value}
      </span>
    </div>
  );
}

const STATS = [
  { value: "₹0.50", label: "Value of 1 TruCoin" },
  { value: "5%", label: "Back on prepaid orders" },
  { value: "50", label: "Coins per approved review" },
  { value: "180 days", label: "Validity after coins unlock" },
];

function StatsStrip() {
  return (
    <section className="border-y border-border bg-white/50 py-10">
      <div className="container mx-auto grid grid-cols-2 gap-6 px-4 md:grid-cols-4 md:px-8">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 70}>
            <div className="text-center">
              <p className="font-display text-2xl font-bold text-brand md:text-3xl">{s.value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

type Way = { icon: typeof BadgePercent; title: string; coins: string; body: string; highlight?: boolean };

const WAYS: Way[] = [
  {
    icon: BadgePercent,
    title: "Shop prepaid",
    coins: "5% back",
    body: "Every fully prepaid order earns 5% of the eligible ex-GST value back as TruCoins, up to 500 coins per order.",
  },
  {
    icon: Gift,
    title: "Write a review",
    coins: "+50 coins",
    body: "Share your experience on a product you bought and 50 TruCoins are credited automatically.",
  },
  {
    icon: Users,
    title: "Refer a friend",
    coins: "+200 coins",
    body: "They get ₹100 off plus 200 signup coins, and you earn 200 TruCoins once their first order is delivered.",
  },
];

function TierLadder() {
  return (
    <section className="section-y container mx-auto px-4 md:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">
            Ways to earn
          </span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-deep md:text-4xl">
            Three ways your coins add up
          </h2>
          <p className="mt-3 text-muted-foreground">
            Stack them across the year, coins from orders, reviews and referrals all land in the
            same balance.
          </p>
        </div>
      </Reveal>

      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {WAYS.map((w, i) => (
          <Reveal key={w.title} delay={i * 80}>
            <div
              className={cn(
                "h-full rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-luxury",
                w.highlight ? "border-brand/30 bg-white shadow-luxury" : "border-border bg-white/70",
              )}
            >
              <div
                className={cn(
                  "flex h-11 w-11 items-center justify-center rounded-full",
                  w.highlight ? "bg-brand text-white" : "bg-brand/10 text-brand",
                )}
              >
                <w.icon className="h-5 w-5" />
              </div>
              <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-brand">
                {w.coins}
              </p>
              <h3 className="mt-1 font-display text-lg font-semibold text-brand-deep">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const STEP_ROWS = [
  {
    n: "01",
    title: "Order prepaid",
    body: "Pay online at checkout. Your coins appear as pending straight away.",
  },
  {
    n: "02",
    title: "Coins unlock",
    body: "7 days after delivery, pending coins become available in your account.",
  },
  {
    n: "03",
    title: "Save at checkout",
    body: "Switch TruCoins on at your next prepaid checkout, the discount applies instantly.",
  },
];

function ThreeStep() {
  return (
    <section className="bg-brand-soft/40 py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          <Reveal>
            <div>
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">
                Simple by design
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-deep md:text-4xl">
                Three steps, zero paperwork
              </h2>
              <p className="mt-4 text-muted-foreground">
                Nothing to claim, nothing to email support about. Your balance, pending coins and
                expiry dates are always visible in your account.
              </p>
              <Link to="/account" className="mt-6 inline-block">
                <Button variant="outline" size="lg" className="rounded-full px-7">
                  View my balance
                </Button>
              </Link>
            </div>
          </Reveal>

          <div className="grid gap-4">
            {STEP_ROWS.map((s, i) => (
              <Reveal key={s.n} delay={100 + i * 90}>
                <div className="flex items-start gap-4 rounded-2xl border border-border bg-white p-5">
                  <span className="font-display text-2xl font-bold text-brand/30">{s.n}</span>
                  <div>
                    <h3 className="font-display text-base font-semibold text-brand-deep">
                      {s.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ReferBand() {
  return (
    <section className="relative overflow-hidden bg-brand-deep py-16 text-white md:py-24">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(700px 500px at 22% 18%, rgba(30,94,255,0.28), transparent 60%), radial-gradient(520px 420px at 84% 82%, rgba(30,94,255,0.16), transparent 60%)",
        }}
      />
      <div className="container relative mx-auto px-4 text-center md:px-8">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] backdrop-blur">
            <Users className="h-3.5 w-3.5" />
            Refer &amp; Earn (Beta)
          </span>
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight md:text-4xl">
            You both get 200 TruCoins.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-white/70">
            Share your referral link. When a friend places their first eligible prepaid order of
            ₹1,000+ ex-GST, they get ₹100 off plus 200 signup TruCoins, and you earn 200 TruCoins,
            credited 7 days after their delivery.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/referral">
              <Button size="lg" variant="secondary" className="rounded-full px-7">
                Get my referral link
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <p className="mt-5 text-xs text-white/50">
            Max 5 qualified referrals per rolling 30-day window. Beta terms apply.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

const TRUST = [
  {
    icon: ShieldCheck,
    title: "No wallet, no lock-in",
    body: "TruCoins are a straightforward discount on eligible products, never a stored-value balance you have to top up.",
  },
  {
    icon: Clock,
    title: "Clear expiry",
    body: "Coins stay valid for 180 days after they unlock, and every expiry date is shown in your account.",
  },
  {
    icon: Coins,
    title: "Fair caps, stated upfront",
    body: "Redeem 20-500 coins per order, capped at 20% of eligible ex-GST value. Nothing hidden in the footnotes.",
  },
];

function TrustGrid() {
  return (
    <section className="section-y container mx-auto px-4 md:px-8">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">
            The fine print, in plain English
          </span>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-brand-deep md:text-4xl">
            Rewards without the catch
          </h2>
        </div>
      </Reveal>
      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {TRUST.map((t, i) => (
          <Reveal key={t.title} delay={i * 80}>
            <div className="glass-panel h-full rounded-2xl p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand/10 text-brand">
                <t.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-brand-deep">{t.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="border-t border-border bg-white/50 py-16 md:py-24">
      <div className="container mx-auto px-4 text-center md:px-8">
        <Reveal>
          <div className="mx-auto flex max-w-2xl flex-col items-center">
            <TruCoinBadge size="md" className="mb-6" />
            <h2 className="font-display text-3xl font-bold tracking-tight text-brand-deep md:text-4xl">
              Your next order is already earning
            </h2>
            <p className="mt-3 text-muted-foreground">
              Shop prepaid today and watch the coins land in your account.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
              <Link to="/shop">
                <Button size="lg" className="rounded-full px-7">
                  Shop TruWe
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/trucoins">
                <Button variant="outline" size="lg" className="rounded-full px-7">
                  Full programme details
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">
              Read the{" "}
              <Link to="/policies/trucoins-terms" className="text-brand underline">
                TruCoins Programme Terms
              </Link>
              .
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
